import { createSelector } from 'reselect'

import { allBooksSelector, allBooksAsArraySelector } from './booksFirstOrder'
import { allCardsSelector } from './cardsFirstOrder'
import { charactersSortedAtoZSelector } from './charactersFirstOrder'
import { allNotesSelector } from './notesFirstOrder'
import { placesSortedAtoZSelector } from './placesFirstOrder'
import { currentViewSelector, allHierarchyLevelsSelector } from './secondOrder'
import { allLinesSelector } from './linesFirstOrder'
import { allBeatsSelector } from './beatsFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Books} Books
 * @typedef {import('../index').Book} Book
 * @typedef {import('../index').PlottrFileBeatsEntry} Beats
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').Line} Line
 * @typedef {import('../types/system').BookWithAllAssociatedData} BookWithAllAssociatedData
 * @typedef {import('../types/system').HierarchyLevels} HierarchyLevelsForOneFile
 */

/**
 * @type {function(PlottrState): Books}
 */
export const booksFilterItemsSelector = createSelector(
  currentViewSelector,
  allBooksSelector,
  placesSortedAtoZSelector,
  allNotesSelector,
  charactersSortedAtoZSelector,
  allCardsSelector,
  (currentView, books, places, notes, characters, cards) => {
    const series = { series: { id: 'series', title: 'Series' } }
    /**
     * @type {Books}
     */
    const allBooksWithSeries = Object.assign({}, series, books)
    switch (currentView) {
      case 'notes': {
        const filteredBooks = Object.values(allBooksWithSeries)
          .filter((book) => {
            return (
              !Array.isArray(book) &&
              notes.some((note) => {
                return note.bookIds.indexOf(book?.id) !== -1
              })
            )
          })
          .reduce((acc, next) => {
            if (!Array.isArray(next) && next.id) {
              return {
                ...acc,
                [next.id]: next,
              }
            }
            return acc
          }, {})

        const allIds = Object.keys(filteredBooks).reduce((acc, key) => {
          try {
            const bookId = parseInt(key)
            if (bookId) {
              return [...acc, bookId]
            } else {
              return acc
            }
          } catch (_e) {
            return acc
          }
        }, [])
        /**
         * @type {Books}
         */
        // @ts-ignore
        const result = Object.assign({}, { ...filteredBooks, allIds })
        return result
      }
      case 'places': {
        const filteredBooks = Object.values(allBooksWithSeries)
          .filter((book) => {
            return (
              !Array.isArray(book) &&
              places.some((place) => {
                return place?.bookIds?.indexOf(book?.id) !== -1
              })
            )
          })
          .reduce((acc, next) => {
            if (!Array.isArray(next) && next.id) {
              return {
                ...acc,
                [next.id]: next,
              }
            }
            return acc
          }, {})

        const allIds = Object.keys(filteredBooks)
        /**
         * @type {Books}
         */
        // @ts-ignore
        const result = Object.assign({}, { ...filteredBooks, allIds })
        return result
      }
      case 'characters': {
        const filteredBooks = Object.values(allBooksWithSeries)
          .filter((book) => {
            return (
              !Array.isArray(book) &&
              characters.some((character) => {
                return character?.bookIds?.indexOf(book?.id) !== -1
              })
            )
          })
          .reduce((acc, next) => {
            if (!Array.isArray(next) && next.id) {
              return {
                ...acc,
                [next.id]: next,
              }
            }
            return acc
          }, {})

        const allIds = Object.keys(filteredBooks)
        /**
         * @type {Books}
         */
        // @ts-ignore
        const result = Object.assign({}, { ...filteredBooks, allIds })
        return result
      }
      case 'timeline': {
        const filteredBooks = Object.values(allBooksWithSeries)
          .filter((book) => {
            return (
              !Array.isArray(book) &&
              cards.some((card) => {
                return card?.bookId === book?.id
              })
            )
          })
          .reduce((acc, next) => {
            if (!Array.isArray(next) && next.id) {
              return {
                ...acc,
                [next.id]: next,
              }
            }
            return acc
          }, {})

        const allIds = Object.keys(filteredBooks)
        /**
         * @type {Books}
         */
        // @ts-ignore
        const result = Object.assign({}, { ...filteredBooks, allIds })
        return result
      }
      default: {
        /**
         * @type {Books}
         */
        // @ts-ignore
        const result = {
          allIds: [],
        }
        return result
      }
    }
  }
)

/**
 * @type {function(PlottrState): Array<BookWithAllAssociatedData>}
 */
export const allBooksWithAllAssociatedDataSelector = createSelector(
  allBooksAsArraySelector,
  allCardsSelector,
  allLinesSelector,
  allBeatsSelector,
  allHierarchyLevelsSelector,
  (books, cards, lines, beats, hierarchyLevels) => {
    return books.map((book) => {
      const booksLines = lines.filter(({ bookId }) => {
        return bookId == book.id
      })
      const booksCards = cards.filter(({ lineId }) => {
        return booksLines.some(({ id }) => {
          return id === lineId
        })
      })
      const booksBeats = beats[book.id]
      const booksHierarchy = hierarchyLevels[book.id]
      return {
        book,
        lines: booksLines,
        cards: booksCards,
        beats: booksBeats,
        hierarchy: booksHierarchy,
      }
    })
  }
)
